import { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from "../components/ui/radiogroup";
import { ArrowRight, Check, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/auth';
import axios from 'axios';
import toast from 'react-hot-toast';

type BillingPeriod = 'monthly' | 'yearly';

interface PriceTier {
  credits: number;
  uses: number;
  price: number;
  perCredit: number;
}

interface ExtensionProduct {
  price: number;
  variantId: string;
}

interface PriceData {
  paygo: PriceTier[];
  subscription: PriceTier[];
  extension: ExtensionProduct;
}

interface VariantIds {
  paygo: Record<number, string>;
  subscription: Record<number, string>;
  yearly: Record<number, string>;
  extension: string;
}

const VARIANT_IDS : VariantIds = {
  paygo: {
    50: '665133',
    100: '665135',
    300: '665136',
  },
  subscription: {
    100: '665138',
    300: '665139',
    1000: '665140',
  },
  yearly: {
    100: '665158',
    300: '665159',
    1000: '665160',
  },
  extension: '705171', // Replace with actual variant ID
};

const priceData: PriceData = {
  paygo: [
    { credits: 50, uses: 25, price: 5, perCredit: 0.10 },
    { credits: 100, uses: 50, price: 20, perCredit: 0.20 },
    { credits: 300, uses: 150, price: 45, perCredit: 0.15 }
  ],
  subscription: [
    { credits: 100, uses: 50, price: 11, perCredit: 0.11 },
    { credits: 300, uses: 150, price: 30, perCredit: 0.10 },
    { credits: 1000, uses: 500, price: 90, perCredit: 0.09 }
  ],
  extension: {
    price: 49,
    variantId: VARIANT_IDS.extension
  }
};

const PricingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isExtensionLoading, setIsExtensionLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshJWT, isAuthenticated } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [selectedPayGoTier, setSelectedPayGoTier] = useState<string>('50');
  const [selectedSubscriptionTier, setSelectedSubscriptionTier] = useState<string>('100');

  const backendUrl = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:3004' 
    : process.env.REACT_APP_BACKEND_URL;

  const createAxiosInstance = useCallback(async () => {
    const token = await refreshJWT();
    return axios.create({
      baseURL: backendUrl,
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }, [refreshJWT]);

  const handlePayGoCheckout = async () => {
   
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    const selectedTier = priceData.paygo.find(
      tier => tier.credits.toString() === selectedPayGoTier
    );

    if (!selectedTier) {
      console.error('No tier selected for PayGo checkout');
      return;
    }

    const variantId = VARIANT_IDS.paygo[selectedTier.credits];
  if (!variantId) {
    console.error('Variant ID not found for selected tier');
    return;
  }

  try {
    setIsLoading(true);
    const axiosInstance = await createAxiosInstance();
    const response = await axiosInstance.post('/api/create-checkout', {
      type: 'paygo',
      hours: selectedTier.credits / 2, // Convert credits to hours
      currency: 'USD',
      price: selectedTier.price,
      variantId
    });

    if (response.data.checkoutUrl) {
      window.location.href = response.data.checkoutUrl;
    }

  } catch (error) {
    console.error('Checkout error:', error);
    toast.error('Failed to create checkout. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

const handleSubscriptionCheckout = async () => {
  if (!isAuthenticated) {
    navigate('/auth');
    return;
  }

  const selectedTier = priceData.subscription.find(
    tier => tier.credits.toString() === selectedSubscriptionTier
  );

  if (!selectedTier) {
    console.error('No tier selected for subscription checkout');
    return;
  }

  // Safely access variant ID based on billing period
  const variantIdMap = billingPeriod === 'yearly' ? VARIANT_IDS.yearly : VARIANT_IDS.subscription;
  const variantId = variantIdMap[selectedTier.credits];
  
  if (!variantId) {
    console.error('Variant ID not found for selected tier');
    return;
  }

  try {
    setIsLoading(true);
    const axiosInstance = await createAxiosInstance();
    const response = await axiosInstance.post('/api/create-checkout', {
      type: 'subscription',
      hours: selectedTier.credits / 2, // Convert credits to hours
      currency: 'USD',
      billingPeriod,
      price: selectedTier.price,
      variantId
    });

   
    if (response.data.checkoutUrl) {
      window.location.href = response.data.checkoutUrl;
    }
  } catch (error) {
    console.error('Checkout error:', error);
    toast.error('Failed to create checkout. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

const handleExtensionCheckout = async () => {
  if (!isAuthenticated) {
    navigate('/auth');
    return;
  }

  try {
    setIsExtensionLoading(true);
    const axiosInstance = await createAxiosInstance();
    const response = await axiosInstance.post('/api/create-checkout', {
      type: 'extension',
      currency: 'USD',
      price: priceData.extension.price,
      variantId: priceData.extension.variantId
    });

    if (response.data.checkoutUrl) {
      window.location.href = response.data.checkoutUrl;
    }
  } catch (error) {
    console.error('Extension checkout error:', error);
    toast.error('Failed to create checkout. Please try again.');
  } finally {
    setIsExtensionLoading(false);
  }
};

  const getAdjustedPrice = (basePrice: number): number => {
    if (billingPeriod === 'yearly') {
      return Math.round(basePrice * 12 * 0.9); // 10% discount for yearly
    }
    return basePrice;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium text-gray-900 mb-6 tracking-tight">
            Simple Pricing Plan
          </h1>
          <p className="text-lg sm:text-xl text-gray-800 max-w-3xl mx-auto font-medium">
            Try it for free. Pay as you grow. Switch at any time.<br className="hidden sm:block" />
            2 credits per use • 10 free credits to start
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Free Trial Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-200">
            <div className="h-full flex flex-col">
              <h3 className="text-2xl sm:text-3xl font-medium text-indigo-600 mb-4 tracking-tight">Free Trial</h3>
              <p className="text-base sm:text-lg text-gray-700 mb-8 font-medium">
                Start with 10 credits (5 uses) free.<br />
                No credit card required.
              </p>
              <div className="mt-auto">
                {isAuthenticated ? (
                  <Button 
                    variant="outline"
                    className="w-full py-5 sm:py-7 text-base sm:text-lg font-semibold rounded-xl border-2 text-gray-900 hover:bg-gray-50 justify-between group"
                    onClick={() => navigate('/dashboard')}
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                ) : (
                  <Button 
                    variant="outline"
                    className="w-full py-5 sm:py-7 text-base sm:text-lg font-semibold rounded-xl border-2 text-gray-900 hover:bg-gray-50 justify-between group"
                    onClick={() => navigate('/auth')}
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Pay as You Go Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border border-indigo-200 relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-indigo-600 text-white px-4 sm:px-6 py-1 sm:py-2 rounded-full text-sm sm:text-base font-semibold">
                RECOMMENDED
              </span>
            </div>
            <div className="h-full flex flex-col">
              <h3 className="text-2xl sm:text-3xl font-medium text-indigo-600 mb-4 tracking-tight">Pay as You Go</h3>
              <p className="text-base sm:text-lg text-gray-700 mb-6 font-medium">
                Flexible pricing for occasional use. Credits valid for 2 years.
              </p>
              <RadioGroup 
                value={selectedPayGoTier}
                onValueChange={setSelectedPayGoTier}
                className="space-y-4 mb-8"
              >
                {priceData.paygo.map((tier) => (
                  <label 
                    key={tier.credits}
                    htmlFor={`paygo-${tier.credits}`}
                    className="block cursor-pointer"
                  >
                    <div 
                      className={`relative flex items-center justify-between p-4 sm:p-5 rounded-xl transition-all ${
                        selectedPayGoTier === tier.credits.toString()
                          ? 'bg-indigo-50 border-2 border-indigo-200'
                          : 'border-2 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <RadioGroupItem value={tier.credits.toString()} id={`paygo-${tier.credits}`} />
                        <div>
                          <div className="text-lg sm:text-xl font-medium text-gray-900">{tier.credits} CREDITS</div>
                          <div className="text-sm sm:text-base text-gray-700">≈ {tier.uses} uses</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl sm:text-4xl font-medium text-gray-900">
                          ${tier.price}
                        </div>
                        <div className="text-sm sm:text-base text-gray-700">
                          ${tier.perCredit}/credit
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
              <div className="mt-auto">
              <Button 
  onClick={handlePayGoCheckout}
  className="w-full py-5 sm:py-7 text-base sm:text-lg font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
  disabled={isLoading}
>
  {isLoading ? (
    <div className="flex items-center justify-center">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
      Processing...
    </div>
  ) : (
    'Buy Credits'
  )}
</Button>
              </div>
            </div>
          </div>

          {/* Subscription Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-200">
        <div className="h-full flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <h3 className="text-2xl sm:text-3xl font-medium text-indigo-600 tracking-tight mb-2 sm:mb-0">Subscription</h3>
            <div className="inline-flex bg-gray-100 rounded-full p-0.5">
              {(['monthly', 'yearly'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setBillingPeriod(period)}
                  className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full transition-all ${
                    billingPeriod === period
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <p className="text-base sm:text-lg text-gray-700 mb-6 font-medium">
            Credits are usable anytime within the subscription period. 
            Unused credits roll over, up to three times your plan limit.
          </p>
          <RadioGroup 
            value={selectedSubscriptionTier}
            onValueChange={setSelectedSubscriptionTier}
            className="space-y-4 mb-8"
          >
            {priceData.subscription.map((tier) => (
              <label
                key={tier.credits}
                htmlFor={`sub-${tier.credits}`}
                className="block cursor-pointer"
              >
                <div
                  className={`relative flex items-center justify-between p-4 sm:p-5 rounded-xl transition-all ${
                    selectedSubscriptionTier === tier.credits.toString()
                      ? 'bg-indigo-50 border-2 border-indigo-200'
                      : 'border-2 border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-4 min-w-[40%]">
                    <RadioGroupItem value={tier.credits.toString()} id={`sub-${tier.credits}`} />
                    <div>
                      <div className="text-lg sm:text-xl font-medium text-gray-900">{tier.credits} CREDITS</div>
                      <div className="text-sm sm:text-base text-gray-700">≈ {tier.uses} uses per month</div>
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end">
                    <div className="flex items-center gap-2">
                      <div className="text-2xl sm:text-4xl font-medium text-gray-900">
                        ${getAdjustedPrice(tier.price)}
                      </div>
                      {/* {billingPeriod === 'yearly' && (
                        <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full whitespace-nowrap">
                          Save 10%
                        </span>
                      )} */}
                    </div>
                    <div className="text-sm sm:text-base text-gray-700 mt-1">
                      ${tier.perCredit}/credit
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </RadioGroup>
          <div className="mt-auto">
          <Button 
  onClick={handleSubscriptionCheckout}
  className="w-full py-5 sm:py-7 text-base sm:text-lg font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
  disabled={isLoading}
>
  {isLoading ? (
    <div className="flex items-center justify-center">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
      Processing...
    </div>
  ) : (
    'Subscribe Now'
  )}
</Button>
          </div>
        </div>
      </div>
        </div>

        {/* Extension Section */}
        <div className="mt-16 sm:mt-24">
          <h2 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-8 text-center tracking-tight">
            Extension Access
          </h2>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border border-indigo-200">
              <div className="h-full flex flex-col">
                <h3 className="text-2xl sm:text-3xl font-medium text-indigo-600 mb-4 tracking-tight">Browser Extension</h3>
                <p className="text-base sm:text-lg text-gray-700 mb-8 font-medium">
                  Get lifetime access to our powerful browser extension with a one-time payment.
                  Enhance your experience with additional features and capabilities.
                </p>
                <div className="flex items-center justify-between p-4 sm:p-6 bg-indigo-50 rounded-xl border-2 border-indigo-200 mb-8">
                  <div>
                    <div className="text-lg sm:text-xl font-medium text-gray-900">LIFETIME ACCESS</div>
                    <div className="text-sm sm:text-base text-gray-700">One-time payment, unlimited use</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl sm:text-4xl font-medium text-gray-900">
                      $49
                    </div>
                    <div className="text-sm sm:text-base text-indigo-600 font-medium">
                      No subscription needed
                    </div>
                  </div>
                </div>
                <div>
                  <Button 
                    onClick={handleExtensionCheckout}
                    className="w-full py-5 sm:py-7 text-base sm:text-lg font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                    disabled={isExtensionLoading}
                  >
                    {isExtensionLoading ? (
                      <div className="flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                        Processing...
                      </div>
                    ) : (
                      'Get Extension'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
